import React, { useState, useCallback, useRef } from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import UserNotification from "util/UserNotification";
import { ShareButton } from "components/common";
import { MenuItem, DeleteMenuItem } from "components/bootstrap";
import EntityShareModal from "components/permissions/EntityShareModal";
import ViewTypeLabel from "views/components/ViewTypeLabel";
import iterateConfirmationHooks from "views/hooks/IterateConfirmationHooks";
import usePaginationQueryParameter from "hooks/usePaginationQueryParameter";
import usePluginEntities from "hooks/usePluginEntities";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import { isAnyPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import { MoreActions } from "components/common/EntityDataTable";
import { useTableFetchContext } from "components/common/PaginatedEntityTable";
import { deleteView } from "views/api/views";
const defaultDashboardDeletionHook = async (view) => (
  // eslint-disable-next-line no-alert
  window.confirm(`Are you sure you want to delete "${view.title}"?`)
);
const _extractErrorMessage = (error) => error && error.additional && error.additional.body && error.additional.body.message ? error.additional.body.message : error;
const usePluggableDashboardActions = (dashboard) => {
  const modalRefs = useRef({});
  const pluggableActions = usePluginEntities("views.components.dashboardActions");
  const availableActions = pluggableActions.filter((action) => action.useCondition ? !!action.useCondition() : true);
  const actions = availableActions.map(({ component: PluggableDashboardAction, key }) => /* @__PURE__ */ React.createElement(
    PluggableDashboardAction,
    {
      key: `dashboard-action-${key}`,
      dashboard,
      modalRef: () => modalRefs.current[key]
    }
  ));
  const actionModals = availableActions.filter(({ modal }) => !!modal).map(({ modal: ActionModal, key }) => /* @__PURE__ */ React.createElement(
    ActionModal,
    {
      key: `dashboard-action-modal-${key}`,
      dashboard,
      ref: (r) => {
        modalRefs.current[key] = r;
      }
    }
  ));
  return { actions, actionModals };
};
const DashboardDeleteAction = ({
  dashboard,
  refetchDashboards,
  hideDelete = false
}) => {
  const { deselectEntity } = useSelectedEntities();
  const paginationQueryParameter = usePaginationQueryParameter();
  const onDashboardDelete = useCallback(async () => {
    const pluginDashboardDeletionHooks = PluginStore.exports("views.hooks.confirmDeletingDashboard");
    const result = await iterateConfirmationHooks(
      [...pluginDashboardDeletionHooks, defaultDashboardDeletionHook],
      dashboard
    );
    if (result) {
      deleteView(dashboard).then(() => {
        UserNotification.success(`Deleting dashboard "${dashboard.title}" was successful!`, "Success!");
        deselectEntity(dashboard.id);
        refetchDashboards();
        paginationQueryParameter.resetPage();
      }).catch((error) => {
        UserNotification.error(`Deleting dashboard failed: ${_extractErrorMessage(error)}`, "Error!");
      });
    }
  }, [dashboard, deselectEntity, refetchDashboards, paginationQueryParameter]);
  return hideDelete ? null : /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: onDashboardDelete });
};
const DashboardActions = ({ dashboard, hideDelete = false, hideShare = false }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableDashboardActions(dashboard);
  const currentUser = useCurrentUser();
  const { refetch } = useTableFetchContext();
  const moreActions = [
    pluggableActions.length ? pluggableActions : null,
    pluggableActions.length && !hideDelete ? /* @__PURE__ */ React.createElement(MenuItem, { divider: true, key: "divider" }) : null,
    isAnyPermitted(currentUser.permissions, [`view:edit:${dashboard.id}`, "view:edit"]) ? /* @__PURE__ */ React.createElement(
      DashboardDeleteAction,
      {
        dashboard,
        refetchDashboards: refetch,
        key: "delete-action",
        hideDelete
      }
    ) : null
  ].filter(Boolean);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, hideShare || /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      bsSize: "xsmall",
      entityId: dashboard.id,
      entityType: "dashboard",
      onClick: () => setShowShareModal(true)
    }
  ), /* @__PURE__ */ React.createElement(MoreActions, null, moreActions), showShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: dashboard.id,
      entityType: "dashboard",
      description: `Search for a User or Team to add as collaborator on this ${ViewTypeLabel({ type: dashboard.type })}.`,
      entityTitle: dashboard.title,
      onClose: () => setShowShareModal(false)
    }
  ), pluggableActionModals);
};
export default DashboardActions;
